import { EQuizMode } from '@constants';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { AiGenerationOption } from './AiGenerationOption';
import { ImportCsvOption } from './ImportCsvOption';
import {
  DescriptionWrapper,
  IconWrapper,
  InfoWrapper,
  OptionWrapper,
  TitleWrapper,
  Wrapper
} from './QuestionGenerationOptions.styles';

import aiGenerationIcon from '@/assets/ai_generation_icon.svg';
import { Icon } from '@/components/Icon';
import { useQuizForm } from '@/hooks';
import { buildQuizUrl } from '@/utils';

const ICON_SIZE = 24;
const IMAGE_SIZE = 48;

type Props = {
  isGeneratingQuestions: boolean;
  isFromBaseContent?: boolean;
};

export const QuestionGenerationOptions: React.FC<Props> = ({
  isGeneratingQuestions,
  isFromBaseContent
}) => {
  const { t } = useTranslation('ManagerQuizzesTranslations');
  const { config, setConfig, setConfigOpen } = useQuizForm();
  const { colors } = useTheme();
  const navigateTo = useNavigate();

  const isAiGenerationEnabled = useMemo(
    () => config.mode === EQuizMode.QUIZ || config.mode === EQuizMode.TRUE_FALSE,
    [config.mode]
  );

  const handleManualQuestionGeneration = () => {
    setConfigOpen(false);
    navigateTo(buildQuizUrl({ isFromBaseContent }));
  };

  const options = useMemo(() => {
    const generationOptions = [
      {
        title: t('MANUAL_QUESTION_GENERATION'),
        description: t('MANUAL_QUESTION_GENERATION_DESCRIPTION'),
        onClick: handleManualQuestionGeneration,
        icon: (
          <IconWrapper $borderColor={colors.purple3} $backgroundColor={colors.purple2}>
            <Icon i="RequestQuote" size={ICON_SIZE} color="purple6" />
          </IconWrapper>
        )
      },
      {
        title: t('IMPORT_FILE'),
        description: t('IMPORT_FILE_DESCRIPTION'),
        onClick: () => setConfig({ ...config, generationType: 'IMPORT' }),
        icon: (
          <IconWrapper $borderColor={colors.orange3} $backgroundColor={colors.orange2}>
            <Icon i="Upload" size={ICON_SIZE} color="orange6" />
          </IconWrapper>
        )
      }
    ];
    if (isAiGenerationEnabled)
      generationOptions.unshift({
        title: t('AI_QUESTION_GENERATION'),
        description: t('AI_QUESTION_GENERATION_DESCRIPTION'),
        onClick: () => setConfig({ ...config, generationType: 'AI' }),
        icon: (
          <img src={aiGenerationIcon} alt="ai-generation" width={IMAGE_SIZE} height={IMAGE_SIZE} />
        )
      });
    return generationOptions;
  }, [isAiGenerationEnabled, config]);

  if (config.generationType === 'AI')
    return <AiGenerationOption isGeneratingQuestions={isGeneratingQuestions} />;

  if (config.generationType === 'IMPORT') return <ImportCsvOption />;

  return (
    <Wrapper>
      {options.map(option => (
        <OptionWrapper key={option.title} onClick={option.onClick}>
          {option.icon}
          <InfoWrapper>
            <TitleWrapper>{option.title}</TitleWrapper>
            <DescriptionWrapper>{option.description}</DescriptionWrapper>
          </InfoWrapper>
        </OptionWrapper>
      ))}
    </Wrapper>
  );
};
